import React, { useState, useMemo } from 'react';
import { Package, Plus, Search, LayoutGrid } from 'lucide-react';
import { useProducts, useAllProducts, type Product } from '../hooks/useProducts';
import { TableContent } from './LayoutPage';
import { useBusinessContext } from '../context/BusinessContext';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { useBusinesses, type Business } from '../hooks/useBusinessAccessType';
import { ProductDetailPanel } from './ProductDetailPanel';
import './businesses.css';
import { useFdCan } from '../hooks/useFdCan';

type GatewayFilter = 'all' | 'carnet' | 'portal' | 'none';

const FILTER_CHIPS: Array<{ id: GatewayFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'portal', label: 'Portal' },
  { id: 'carnet', label: 'Cardnet' },
  { id: 'none', label: 'No gateway' },
];

export const Products: React.FC = () => {
  const { business } = useBusinessContext();
  const businessId = business?.id || null;
  const permissions = useUserPermissions();
  const isAdmin = permissions.isAdmin;
  const canCreate = useFdCan('business', 'create');

  const { data: businessesData } = useBusinesses();
  const businesses: Business[] = useMemo(() => {
    const raw = businessesData as any;
    if (Array.isArray(raw)) return raw;
    if (raw?.data && Array.isArray(raw.data)) return raw.data;
    return [];
  }, [businessesData]);
  const businessMap = useMemo(() => {
    const map: Record<string, string> = {};
    businesses.forEach((b) => { map[b.id] = b.name; });
    return map;
  }, [businesses]);

  const [selectedBusinessFilter, setSelectedBusinessFilter] = useState<string | null>(null);
  // The admin and the tenant Front Desk (no business of its own) see every business, with the picker.
  const seesAll = permissions.seesAllBusinesses;
  const effectiveBusinessId = seesAll ? selectedBusinessFilter : businessId;
  const isShowingAll = seesAll && !selectedBusinessFilter;

  const { data: filteredProducts, isLoading: filteredLoading } = useProducts(effectiveBusinessId);
  const { data: allProducts, isLoading: allLoading } = useAllProducts();
  const products = isShowingAll ? allProducts : filteredProducts;
  const isLoading = isShowingAll ? allLoading : filteredLoading;

  const [searchTerm, setSearchTerm] = useState('');
  const [gatewayFilter, setGatewayFilter] = useState<GatewayFilter>('all');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const displayProducts = useMemo(() => {
    if (!products) return [];
    let list = products;
    if (gatewayFilter === 'carnet') list = list.filter((p) => p.payment_gateway === 'carnet');
    else if (gatewayFilter === 'portal') list = list.filter((p) => p.payment_gateway === 'portal');
    else if (gatewayFilter === 'none') list = list.filter((p) => !p.payment_gateway);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter((p) => p.name?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q));
    }
    return list;
  }, [products, gatewayFilter, searchTerm]);

  // Group: by business when admin viewing all; otherwise no grouping.
  const grouped = useMemo<Array<{ key: string; label: string; items: Product[] }>>(() => {
    if (!isShowingAll) return [{ key: 'all', label: '', items: displayProducts }];
    const map = new Map<string, Product[]>();
    displayProducts.forEach((p) => {
      const k = p.business_id || '__unassigned';
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(p);
    });
    return Array.from(map.entries())
      .map(([k, items]) => ({
        key: k,
        label: k === '__unassigned' ? 'Unassigned' : (businessMap[k] || k),
        items,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [displayProducts, isShowingAll, businessMap]);

  const handleSelect = (p: Product) => {
    setIsCreating(false);
    setSelectedProduct(p);
  };

  const handleAdd = () => {
    setSelectedProduct(null);
    setIsCreating(true);
  };

  const handleCloseDetail = () => {
    setSelectedProduct(null);
    setIsCreating(false);
  };

  const renderItem = (p: Product) => {
    const isSelected = selectedProduct?.id === p.id;
    const gateway = p.payment_gateway;
    return (
      <div
        key={p.id}
        className={`bz-item ${isSelected ? 'is-selected' : ''}`}
        onClick={() => handleSelect(p)}
      >
        <div className="bz-item-avatar">
          <Package size={18} color="#9CA3AF" />
        </div>
        <div className="bz-item-content">
          <div className="bz-item-top">
            <span className="bz-item-name">{p.name || 'Unnamed'}</span>
            <span style={{ fontWeight: 600, fontSize: 13, color: '#111827', flexShrink: 0 }}>
              {p.price != null ? `$${Number(p.price).toFixed(2)}` : '—'}
            </span>
          </div>
          <div className="bz-item-meta" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.description || (isShowingAll ? (businessMap[p.business_id || ''] || '—') : '—')}
            </span>
            {gateway && (
              <span
                style={{
                  fontSize: 11,
                  padding: '1px 8px',
                  borderRadius: 10,
                  fontWeight: 500,
                  flexShrink: 0,
                  backgroundColor: gateway === 'portal' ? '#EEF2FF' : '#FFF7ED',
                  color: gateway === 'portal' ? '#4338CA' : '#C2410C',
                }}
              >
                {gateway}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const detailMode: 'create' | 'edit' = isCreating ? 'create' : 'edit';
  const showDetail = isCreating || !!selectedProduct;
  const businessFilterOptions = [
    { id: '', name: 'All Businesses' },
    ...businesses,
  ];

  return (
    <TableContent>
      <div className="bz-app">
        <aside className="bz-list-pane">
          <header className="bz-list-header">
            <div className="bz-list-title-row">
              <h1 className="bz-list-title">Products</h1>
              <div className="bz-title-right">
                {!isLoading && (products?.length ?? 0) > 0 && (
                  <span className="bz-list-count">{products?.length}</span>
                )}
                {canCreate && (
                  <button type="button" className="bz-add-btn" onClick={handleAdd}>
                    <Plus size={14} />
                    Add
                  </button>
                )}
              </div>
            </div>

            <div className="bz-search">
              <span className="bz-search-icon"><Search size={14} /></span>
              <input
                className="bz-search-input"
                placeholder="Search products…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {seesAll && (
              <select
                className="bz-search-input"
                style={{ paddingLeft: 12 }}
                value={selectedBusinessFilter ?? ''}
                onChange={(e) => {
                  setSelectedBusinessFilter(e.target.value || null);
                  setSelectedProduct(null);
                }}
              >
                {businessFilterOptions.map((b) => (
                  <option key={b.id || 'all'} value={b.id}>{b.name}</option>
                ))}
              </select>
            )}

            <div className="bz-chips">
              {FILTER_CHIPS.map((chip) => (
                <button
                  key={chip.id}
                  type="button"
                  className={`bz-chip ${gatewayFilter === chip.id ? 'is-active' : ''}`}
                  onClick={() => setGatewayFilter(chip.id)}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </header>

          <div className="bz-list-scroll">
            {isLoading ? (
              <div className="bz-list-empty">Loading products…</div>
            ) : displayProducts.length === 0 ? (
              <div className="bz-list-empty">
                <div>No products found</div>
                {!searchTerm && canCreate && (
                  <div style={{ fontSize: 12, marginTop: 4, color: '#9CA3AF' }}>
                    Click "Add" to create your first product.
                  </div>
                )}
              </div>
            ) : (
              grouped.map((g) => (
                <div key={g.key}>
                  {g.label && <div className="bz-group-label">{g.label} · {g.items.length}</div>}
                  {g.items.map(renderItem)}
                </div>
              ))
            )}
          </div>
        </aside>

        <main className="bz-detail-pane">
          {showDetail ? (
            <ProductDetailPanel
              key={isCreating ? 'create' : selectedProduct?.id}
              product={selectedProduct}
              mode={detailMode}
              businessId={effectiveBusinessId || businessId}
              businesses={businesses}
              isAdmin={isAdmin}
              onClose={handleCloseDetail}
            />
          ) : !canCreate && !isLoading && displayProducts.length === 0 ? null : (
            // SAM-1312 (U20): with no products and no way to add one, the list already says so; no second empty state.
            <div className="bz-empty">
              <LayoutGrid size={40} className="bz-empty-icon" />
              <div className="bz-empty-title">Select a product</div>
              <div className="bz-empty-sub">
                {canCreate ? 'Choose a product from the list to view or edit, or add a new one.' : 'Choose a product from the list to view it.'}
              </div>
            </div>
          )}
        </main>
      </div>
    </TableContent>
  );
};
